import { useLazyQuery, useQuery } from '@apollo/client'
import { gql } from '@apollo/client'
import { UploadFile, PhotoCamera, CheckCircle } from '@mui/icons-material'
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Link,
    Stack,
    Typography,
} from '@mui/material'
import IntakeCard from 'candidate/components/IntakeCard'
import { QuestionWrapper } from 'candidate/components/QuestionWrapper'
import config from 'config'
import { createRef, useCallback, useState } from 'react'
import { ErrorCode, useDropzone } from 'react-dropzone'
import NextButton from 'shared/components/NextButton'
import useToast from 'shared/components/Toast/useToast'
import { useDispatch, useSelector } from 'react-redux'
import { selectUser, setUser } from 'shared/state/userSlice'
import { getPxAsRem } from 'shared/utils'

import type { Dispatch, FC } from 'react'

const EXISTING_DOCUMENTS_QUERY = gql`
    query DocumentUploadExistingDocs {
        candidateMe {
            id
            internalProfileImageKey
            documents {
                id
                documentKey
                documentType
            }
            user {
                id
                profileImageKey
            }
        }
    }
`

const DOWNLOAD_DOCUMENT_QUERY = gql`
    query DownloadDocumentForIntake($documentId: String!) {
        download(documentId: $documentId) {
            url
        }
    }
`

interface Props {
    prompt: string
    subtext?: string
    callback: Dispatch<any>
    skipCallback?: Dispatch<any>
    skipText?: string
}

const dropzoneStyle = {
    padding: getPxAsRem(24, 16),
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    outline: 'none',
    transition: 'border .24s ease-in-out',
}

const formatDocumentKey = (docKey: string) => {
    if (docKey.includes('/')) {
        const parts = docKey.split('/')
        return parts[parts.length - 1]
    }
    return docKey
}

const DocumentUploadQuestion: FC<Props> = ({
    prompt,
    subtext,
    callback,
    skipCallback,
    skipText,
}) => {
    const toast = useToast()
    const dispatch = useDispatch()
    const user = useSelector(selectUser)

    const { data: existingData, loading: existingLoading } = useQuery(
        EXISTING_DOCUMENTS_QUERY
    )
    const [downloadDocument] = useLazyQuery(DOWNLOAD_DOCUMENT_QUERY, {
        fetchPolicy: 'network-only',
    })
    const existingResume = existingData?.candidateMe?.documents?.find(
        (doc: any) => doc.documentType === 'Resume'
    )
    const existingProfileImage =
        existingData?.candidateMe?.user?.profileImageKey ||
        existingData?.candidateMe?.internalProfileImageKey

    const [resumeUploaded, setResumeUploaded] = useState(false)
    const [resumeFileName, setResumeFileName] = useState('')
    const [resumeLoading, setResumeLoading] = useState(false)

    const [profilePictureUploaded, setProfilePictureUploaded] = useState(false)
    const [profileImage, setProfileImage] = useState<string | undefined>(
        user?.profileImageKey ?? undefined
    )

    const handleDownloadResume = async (documentId: string) => {
        const { data } = await downloadDocument({ variables: { documentId } })
        if (data?.download?.url) {
            const link = document.createElement('a')
            link.href = data.download.url
            link.target = '_blank'
            link.click()
            link.remove()
        } else {
            toast({
                title: 'Something went wrong',
                message: 'Could not download resume',
            })
        }
    }
    const [imageFile, setImageFile] = useState<File>()
    const [imagePreview, setImagePreview] = useState('')
    const [dialogOpen, setDialogOpen] = useState(false)
    const [imageLoading, setImageLoading] = useState(false)
    const imageInputRef = createRef<HTMLInputElement>()

    const hasResume = resumeUploaded || !!existingResume
    const hasProfilePicture = profilePictureUploaded || !!existingProfileImage

    const uploadResume = async (file: File) => {
        setResumeLoading(true)
        const formData = new FormData()
        formData.append('document', file)
        formData.append('documentType', 'Resume')
        try {
            const response = await fetch(
                `${config.api.uri}/candidateDocumentUpload`,
                {
                    method: 'POST',
                    body: formData,
                    headers: {
                        contentType: 'multipart/form-data',
                        Authorization:
                            'Bearer ' + localStorage.getItem('token'),
                        boundary: '----WebKitFormBoundary7MA4YWxkTrZu0gW',
                    },
                }
            )
            if (response.ok) {
                const data = await response.json()
                const docKey = data?.documentKey ?? file.name
                const displayName = docKey.includes('/')
                    ? docKey.split('/').pop()
                    : docKey
                setResumeUploaded(true)
                setResumeFileName(displayName)
                toast({
                    title: 'Resume Uploaded',
                    message: `${displayName} has been uploaded`,
                    severity: 'success',
                })
            } else {
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
        } catch (error) {
            console.error('Failed to upload resume', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        } finally {
            setResumeLoading(false)
        }
    }

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            uploadResume(acceptedFiles[0])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const resumeAccept = {
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
        'application/pdf': ['.pdf'],
    }

    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept: resumeAccept,
        maxSize: 10 * 1024 * 1024,
        onDrop,
        multiple: false,
    })

    const handleSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setImageFile(file)
            setImagePreview(URL.createObjectURL(file))
            setDialogOpen(true)
        }
    }

    const handleUploadImage = async () => {
        if (!imageFile) return
        setImageLoading(true)
        const formData = new FormData()
        formData.append('picture', imageFile)
        try {
            const response = await fetch(`${config.api.uri}/profilePicture`, {
                method: 'POST',
                body: formData,
                headers: {
                    accept: 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                    contentType: 'multipart/form-data',
                    boundary: '----WebKitFormBoundary7MA4YWxkTrZu0gW',
                },
            })
            const data = await response.json()
            if (response.status === 200 && data?.signedUrl) {
                setProfileImage(data.signedUrl)
                setProfilePictureUploaded(true)
                dispatch(setUser({ ...user, profileImageKey: data.signedUrl }))
                toast({
                    title: 'Profile picture updated',
                    message: '',
                    severity: 'success',
                })
            } else {
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
        } catch (error) {
            console.error('Image upload error:', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        } finally {
            setDialogOpen(false)
            setImageLoading(false)
            if (imageInputRef.current) imageInputRef.current.value = ''
        }
    }

    const closeDialog = () => {
        setDialogOpen(false)
        setImageLoading(false)
        if (imageInputRef.current) imageInputRef.current.value = ''
    }

    const fileRejectionItems = fileRejections.map(({ file, errors }) => (
        <Alert severity="error" key={file.name} sx={{ mt: 1 }}>
            <AlertTitle>Error</AlertTitle>
            {errors.map((e) => {
                if (e.code === ErrorCode.FileInvalidType) {
                    return (
                        <span key={e.code}>
                            {file.name} - File must be .doc, .docx, or .pdf
                        </span>
                    )
                }
                return (
                    <span key={e.code}>
                        {file.name} - {e.message}
                    </span>
                )
            })}
        </Alert>
    ))

    return (
        <IntakeCard
            question={prompt}
            subtext={subtext}
            skipCallback={skipCallback}
            skipText={skipText}
        >
            <QuestionWrapper>
                {existingLoading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Box>
                            <Typography
                                variant="h6"
                                component="h2"
                                sx={{ mb: 1 }}
                            >
                                Resume
                            </Typography>
                            {hasResume ? (
                                <>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{
                                            ...dropzoneStyle,
                                            borderColor: '#4caf50',
                                        }}
                                    >
                                        <CheckCircle color="success" />
                                        {resumeFileName ? (
                                            <Typography>
                                                {resumeFileName}
                                            </Typography>
                                        ) : existingResume ? (
                                            <Link
                                                component="button"
                                                underline="hover"
                                                color="primary"
                                                onClick={() =>
                                                    handleDownloadResume(
                                                        existingResume.id
                                                    )
                                                }
                                            >
                                                {formatDocumentKey(
                                                    existingResume.documentKey
                                                )}
                                            </Link>
                                        ) : (
                                            <Typography>Resume</Typography>
                                        )}
                                    </Stack>
                                    <Box sx={{ ...dropzoneStyle, mt: 1 }}>
                                        <div
                                            {...getRootProps()}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <input {...getInputProps()} />
                                            {resumeLoading ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                <Typography>
                                                    <Link
                                                        underline="hover"
                                                        color="primary"
                                                    >
                                                        Upload a new resume
                                                    </Link>
                                                    &nbsp; to replace
                                                </Typography>
                                            )}
                                        </div>
                                    </Box>
                                </>
                            ) : (
                                <Box sx={dropzoneStyle}>
                                    <div
                                        {...getRootProps()}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <input {...getInputProps()} />
                                        {resumeLoading ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <>
                                                <IconButton size="small">
                                                    <UploadFile color="primary" />
                                                </IconButton>
                                                <Typography
                                                    sx={{ py: getPxAsRem(4) }}
                                                >
                                                    <Link
                                                        underline="hover"
                                                        color="primary"
                                                    >
                                                        Click to upload
                                                    </Link>
                                                    &nbsp; or drag and drop
                                                </Typography>
                                                <Typography>
                                                    .doc, .docx, .pdf
                                                </Typography>
                                            </>
                                        )}
                                    </div>
                                </Box>
                            )}
                            {fileRejectionItems}
                        </Box>

                        <Box>
                            <Typography
                                variant="h6"
                                component="h2"
                                sx={{ mb: 1 }}
                            >
                                Profile Picture
                            </Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                            >
                                <Avatar
                                    src={profileImage || existingProfileImage}
                                    sx={{ width: 80, height: 80 }}
                                />
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<PhotoCamera />}
                                >
                                    {hasProfilePicture
                                        ? 'Change Photo'
                                        : 'Upload Photo'}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".jpg,.jpeg,.png"
                                        onChange={handleSelectImage}
                                        ref={imageInputRef}
                                    />
                                </Button>
                                {hasProfilePicture && (
                                    <CheckCircle color="success" />
                                )}
                            </Stack>
                        </Box>
                    </>
                )}
            </QuestionWrapper>

            <NextButton
                disabled={!hasResume}
                onClickHandler={() => {
                    callback({
                        response: {
                            resumeUploaded: hasResume,
                            profilePictureUploaded: hasProfilePicture,
                        },
                    })
                }}
            />

            <Dialog
                open={dialogOpen}
                onClose={!imageLoading ? closeDialog : undefined}
            >
                <DialogContent
                    sx={{
                        padding: getPxAsRem(32),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: getPxAsRem(24),
                        minWidth: getPxAsRem(300),
                        minHeight: getPxAsRem(300),
                    }}
                >
                    {imageLoading ? (
                        <>
                            <CircularProgress />
                            <Typography variant="h6" component="h1">
                                Uploading profile picture
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Avatar
                                src={imagePreview}
                                sx={{ width: 180, height: 180 }}
                            />
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    onClick={closeDialog}
                                >
                                    Cancel
                                </Button>
                                <Button onClick={handleUploadImage}>
                                    Upload Image
                                </Button>
                            </Stack>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </IntakeCard>
    )
}

export default DocumentUploadQuestion
